import { type BasicServiceFragment, ServiceCategory } from '@drycut/graphql';
import { CheckBox, Para, Stack } from '@drycut/ui-components';
import { formattedDuration } from '@drycut/ui-format-utils';

type Props = {
  onServicesSelect: (services: Array<BasicServiceFragment>) => void;
  selectedServices: Array<BasicServiceFragment>;
  services: Array<BasicServiceFragment>;
};

// Groups haircut services before any other services, keeping each group's original (server-side)
// order otherwise. Used both to render the list and to pick a sensible default selection.
export function sortServicesForSelection(
  services: Array<BasicServiceFragment>,
): Array<BasicServiceFragment> {
  return [...services].sort((a, b) => {
    if (a.category === b.category) return 0;

    return a.category === ServiceCategory.Haircut ? -1 : 1;
  });
}

// Lets the customer/stylist pick which services are included in the booking. There can only be
// one haircut service selected at a time (it replaces any previous haircut selection), while any
// number of additional services can be toggled on or off. The caller is responsible for seeding
// an initial selection (eg. the first haircut service) if one is required.
export function ServicesSelection({
  services,
  selectedServices,
  onServicesSelect,
}: Props): React.ReactElement {
  const haircutServices = services.filter(
    (service) => service.category === ServiceCategory.Haircut,
  );
  const otherServices = services.filter((service) => service.category !== ServiceCategory.Haircut);

  const isSelected = (serviceId: string): boolean => {
    return selectedServices.some((service) => service.id === serviceId);
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const serviceId = event.target.name;
    const service = services.find((service) => service.id === serviceId);

    if (!service) return;

    const selectedWithoutHaircut = selectedServices.filter(
      (service) => service.category !== ServiceCategory.Haircut,
    );

    if (event.target.checked) {
      if (service.category === ServiceCategory.Haircut) {
        onServicesSelect([...selectedWithoutHaircut, service]);
      } else {
        onServicesSelect([...selectedServices, service]);
      }
    } else {
      onServicesSelect(selectedServices.filter((service) => service.id !== serviceId));
    }
  };

  const renderService = (service: BasicServiceFragment) => (
    <CheckBox
      key={service.id}
      onChange={handleChange}
      checked={isSelected(service.id)}
      name={service.id}
      label={
        <Stack direction="col">
          <Para as="span" weight="medium" size="sm">
            {service.name}
          </Para>

          <Para as="span" size="xs">
            {service.price} CHF &mdash; {formattedDuration(service.duration, { showCa: true })}
          </Para>
        </Stack>
      }
    />
  );

  return (
    <Stack gap={2}>
      {haircutServices.map(renderService)}

      {haircutServices.length > 0 && otherServices.length > 0 && (
        <hr className="border-t border-gray-200" />
      )}

      {otherServices.map(renderService)}
    </Stack>
  );
}
